/**
 * AI Agent Registry
 *
 * Central registry for all AI agents in the system
 */

import type { AIAgent, AgentMetricsData } from './types.js';

// =============================================================================
// REGISTRY STORAGE
// =============================================================================

const agents: Map<string, AIAgent> = new Map();
const metricsStore: Map<string, AgentMetricsData> = new Map();

// =============================================================================
// REGISTRY FUNCTIONS
// =============================================================================

/**
 * Register a new agent
 */
export function registerAgent(agent: AIAgent): void {
  agents.set(agent.id, agent);

  // Initialize metrics if not exists
  if (!metricsStore.has(agent.id)) {
    metricsStore.set(agent.id, {
      totalCalls: 0,
      successCount: 0,
      errorCount: 0,
      totalLatencyMs: 0,
      corrections: 0,
    });
  }
}

/**
 * Get an agent by ID
 */
export function getAgent(id: string): AIAgent | undefined {
  return agents.get(id);
}

/**
 * Get all registered agents
 */
export function getAllAgents(): AIAgent[] {
  return Array.from(agents.values());
}

/**
 * Get enabled agents only
 */
export function getEnabledAgents(): AIAgent[] {
  return Array.from(agents.values()).filter((a) => a.enabled);
}

/**
 * Enable or disable an agent
 */
export function setAgentEnabled(id: string, enabled: boolean): boolean {
  const agent = agents.get(id);
  if (!agent) return false;
  agent.enabled = enabled;
  return true;
}

/**
 * Get agent metrics
 */
export function getAgentMetrics(id: string): AgentMetricsData | undefined {
  return metricsStore.get(id);
}

/**
 * Update agent metrics after a call
 */
export function updateAgentMetrics(
  id: string,
  success: boolean,
  latencyMs: number
): void {
  const metrics = metricsStore.get(id);
  if (!metrics) return;

  metrics.totalCalls++;
  if (success) {
    metrics.successCount++;
  } else {
    metrics.errorCount++;
  }
  metrics.totalLatencyMs += latencyMs;
  metrics.lastCallAt = new Date().toISOString();
}

/**
 * Record a correction
 */
export function recordCorrection(agentId: string): void {
  const metrics = metricsStore.get(agentId);
  if (!metrics) return;
  metrics.corrections++;
}

/**
 * Calculate computed metrics
 */
export function computeMetrics(id: string) {
  const data = metricsStore.get(id);
  if (!data) return null;

  return {
    totalCalls: data.totalCalls,
    successRate: data.totalCalls > 0 ? data.successCount / data.totalCalls : 0,
    avgLatencyMs: data.totalCalls > 0 ? data.totalLatencyMs / data.totalCalls : 0,
    lastCallAt: data.lastCallAt,
    correctionRate: data.totalCalls > 0 ? data.corrections / data.totalCalls : 0,
  };
}

/**
 * Find agents by trigger event
 */
export function findAgentsByTrigger(eventType: string): AIAgent[] {
  return Array.from(agents.values()).filter(
    (a) => a.enabled && a.triggers.includes(eventType)
  );
}

/**
 * Find agents by capability
 */
export function findAgentsByCapability(capabilityId: string): AIAgent[] {
  return Array.from(agents.values()).filter(
    (a) => a.enabled && a.capabilities.some((c) => c.id === capabilityId)
  );
}
